/* eslint-disable react/no-danger */
import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import Collapsible from 'react-collapsible';

import Modal from 'components/Core/Modal';
import Cta from 'components/Core/Cta';
import Toogle from 'components/Core/Toggle';

import { AppContext } from 'context/AppContext';
import IconSvg from 'components/Core/IconSvg';
import { setSqzlCookieConsent } from 'utils/tracking';

const CookieModal = ({
  preferenceTitle,
  preferenceText,
  preferenceCtaTitle,
  disclaimer,
  essentialCookieText,
  targetingCookieText,
  thirdPartyCookieText,
}) => {
  const [cookies, setCookies] = useState({
    all: false,
    essential: true,
    analytics: false,
    thirdParty: false,
  });

  const [dropdowns, setDropdowns] = useState({
    essential: false,
    analytics: false,
    thirdParty: false,
  });
  const { updateAppState } = useContext(AppContext);

  const closeModal = () => {
    document.body.classList.remove('modal-open');
    updateAppState((state) => ({
      ...state,
      openModal: false,
      showCookieModal: false,
    }));
  };

  const onToggle = (value) => {
    setCookies({
      ...cookies,
      all: false,
      [value]: !cookies[value],
    });
  };

  const onToggleAll = () => {
    setCookies({
      all: !cookies.all,
      essential: true,
      analytics: !cookies.all,
      thirdParty: !cookies.all,
    });
  };

  const setCookiePreferences = () => {
    closeModal();

    updateAppState((state) => ({
      ...state,
      hideCookieBar: true,
    }));

    // Essential cookies are always on
    Cookies.set('sfnAcceptEssential', true);

    if (cookies.analytics) {
      Cookies.set('sfnAcceptAnalytics', true);
    }
    if (cookies.thirdParty) {
      Cookies.set('sfnAcceptThirdParty', true);
    }

    return true;
  };

  const getDropdownArrow = (isOpen, onToggleDropdown) => (
    <button
      className="u-no-tap-highlight focus:outline-none"
      onClick={onToggleDropdown}
      type="button"
      aria-label={isOpen ? 'dropdown sluiten' : 'dropdown openen'}
    >
      <IconSvg icon={isOpen ? 'arrow-up' : 'arrow-down'} size={15} className="text-primary ml-4" />
    </button>
  );
  if (cookies.analytics && cookies.thirdParty) {
    cookies.all = true;
  } else {
    cookies.all = false;
  }
  return (
    <Modal>
      <h4 className="mb-8">{preferenceTitle}</h4>
      <div className="mb-8">{preferenceText}</div>

      <div>
        <div className="flex items-center">
          <Toogle
            value="essential"
            checked={cookies.essential}
            label="Essentiële cookies"
            onToggle={onToggle}
            disabled
          />
          {getDropdownArrow(dropdowns.essential, () => setDropdowns({ essential: !dropdowns.essential }))}
        </div>
        <Collapsible open={dropdowns.essential} easing="cubic-bezier(0.25, 1, 0.5, 1)">
          <div dangerouslySetInnerHTML={{ __html: essentialCookieText }} />
        </Collapsible>
      </div>

      {targetingCookieText && (
        <div>
          <div className="flex items-center">
            <Toogle value="analytics" checked={cookies.analytics} label="Analytics cookies" onToggle={onToggle} />
            {getDropdownArrow(dropdowns.analytics, () => setDropdowns({ analytics: !dropdowns.analytics }))}
          </div>
          <Collapsible open={dropdowns.analytics} easing="cubic-bezier(0.25, 1, 0.5, 1)">
            <div dangerouslySetInnerHTML={{ __html: targetingCookieText }} />
          </Collapsible>
        </div>
      )}

      {thirdPartyCookieText && (
        <div>
          <div className="flex items-center">
            <Toogle value="thirdParty" checked={cookies.thirdParty} label="Third party cookies" onToggle={onToggle} />
            {getDropdownArrow(dropdowns.thirdParty, () => setDropdowns({ thirdParty: !dropdowns.thirdParty }))}
          </div>
          <Collapsible open={dropdowns.thirdParty} easing="cubic-bezier(0.25, 1, 0.5, 1)">
            <div dangerouslySetInnerHTML={{ __html: thirdPartyCookieText }} />
          </Collapsible>
        </div>
      )}

      <Toogle value="all" checked={cookies.all} label="Alles selecteren" onToggle={onToggleAll} />

      <Cta
        showIcon={false}
        className="my-8"
        onClick={() => {
          setCookiePreferences();
          setSqzlCookieConsent();
        }}
      >
        {preferenceCtaTitle}
      </Cta>
      <div>{disclaimer}</div>
    </Modal>
  );
};

CookieModal.propTypes = {
  preferenceTitle: PropTypes.string.isRequired,
  preferenceText: PropTypes.node.isRequired,
  preferenceCtaTitle: PropTypes.string,
  disclaimer: PropTypes.string,
  essentialCookieText: PropTypes.string.isRequired,
  targetingCookieText: PropTypes.string,
  thirdPartyCookieText: PropTypes.string,
};

CookieModal.defaultProps = {
  preferenceCtaTitle: 'Opslaan',
  disclaimer: '',
  targetingCookieText: '',
  thirdPartyCookieText: '',
};

export default CookieModal;
